import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clientDiagnosisActions } from 'store';
import {
    DiagnosisMatchStatus,
    Images,
    PermissionList,
    RolePermission,
    useDiagnosisMatchEnabled,
    useSecondaryModal,
} from 'utils';
import { AddDiagnosisModal } from '../../../../client/clientSingle/clientClinical/diagnoses/addDiagnosisModal';

/** The mismatch sentence, shared by the inline alert and the warnings / errors list. */
export const diagnosisMismatchText = (match) =>
    match?.clientCodes?.length
        ? `The client's diagnoses (${match.clientCodes.join(', ')}) aren't accepted by this service (${match.acceptedCodes.join(', ')}).`
        : `The client has no diagnosis accepted by this service (${match?.acceptedCodes?.join(', ')}).`;

/**
 * Under "Service" in the appointment modal (diagnosis matching on): what the appointment will bill,
 * or why it can't be billed — with a one-step fix for users who manage client diagnoses.
 * Nothing is saved on the appointment; this is the same match the api runs on save and at billing.
 */
export const ApptDiagnosis = ({ clientId, authorizedServiceId, date }) => {
    const dispatch = useDispatch();
    const enabled = useDiagnosisMatchEnabled();
    const match = useSelector((state) => state.clientDiagnosis.diagnosisMatch);
    const { openSecondary } = useSecondaryModal();
    const canManage = RolePermission([PermissionList.CLIENT_DIAGNOSIS_MANAGE?.code]);
    const canRead = canManage || RolePermission([PermissionList.CLIENT_DIAGNOSIS_READ?.code]);
    const params = clientId && authorizedServiceId && date ? { clientId, authorizedServiceId, date } : null;
    const refresh = () => params && dispatch(clientDiagnosisActions.getDiagnosisMatch(params));

    useEffect(() => {
        if (!enabled) return;
        if (params) refresh();
        else dispatch(clientDiagnosisActions.clearDiagnosisMatch());
    }, [enabled, clientId, authorizedServiceId, date]);

    useEffect(() => () => dispatch(clientDiagnosisActions.clearDiagnosisMatch()), []);

    if (!enabled || !params || !match) return null;

    if (match.status === DiagnosisMatchStatus.MATCH) {
        if (!match.codes?.length) return null; // no diagnosis read permission
        return (
            <div className="diagnosis-codes-line">
                <span>Diagnosis</span>
                {match.codes.map((code) => (
                    <span key={code} className="diagnosis-code-chip">
                        {code}
                    </span>
                ))}
            </div>
        );
    }

    const isMismatch = match.status === DiagnosisMatchStatus.MISMATCH;
    // Design (Service / Mobile screens): "Diagnosis mismatch  Client has: … Service accepts: …"
    return (
        <div className="diagnosis-inline-alert">
            <div className="diagnosis-inline-alert-text">
                <img src={Images.alertWarning} alt="warning" />
                {isMismatch ? (
                    <p>
                        <b>Diagnosis mismatch</b>
                        {/* The api sends no client codes without diagnosis read — don't show "none" then */}
                        {canRead && <span>{`Client has: ${match.clientCodes?.length ? match.clientCodes.join(', ') : 'none'}`}</span>}
                        <span>{`Service accepts: ${match.acceptedCodes?.join(', ')}`}</span>
                    </p>
                ) : (
                    <p>
                        <b>No diagnosis</b>
                        <span>Neither the client nor this service has a diagnosis code.</span>
                    </p>
                )}
            </div>
            {canManage && (
                <button
                    type="button"
                    onClick={() =>
                        openSecondary(
                            <AddDiagnosisModal
                                secondary
                                clientId={clientId}
                                acceptedCodes={match.acceptedCodes}
                                defaultStartDate={date}
                                onSaved={refresh}
                            />
                        )
                    }
                >
                    + Add Client Diagnosis Code
                </button>
            )}
        </div>
    );
};
